import React from "react";
import { Box } from "@mui/material";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import BarChartIcon from "@mui/icons-material/BarChart";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { AYUDA_CATEGORIAS } from "../../content/ayudaArticulos";

// Ver TAREA: centro de ayuda del CRM (Fase 4 -- pantalla). Mismo patrón que
// ArticuloContenido.jsx de Citas: este componente es el único lugar que lee
// la forma de un artículo (contexto/pasos/rama/verTambien) y decide cómo
// pintarla -- pages/Ayuda/index.js (índice + layout) lo consume tanto en el
// panel derecho de escritorio como en la pantalla de artículo de móvil, así
// que un artículo se ve exactamente igual en los dos.
//
// A diferencia de Citas, este componente NO tiene "¿Te sirvió?" -- no se
// pidió, y no hay un endpoint de feedback en el backend del CRM para
// persistirlo. Si se agrega más adelante, va aquí, siguiendo el mismo
// patrón que ArticuloContenido.jsx de Citas (api.get/post a
// /ayuda/feedback).
export const CATEGORIA_ICONOS = {
  atencion: HeadsetMicIcon,
  analisis: BarChartIcon,
  automatizacion: BoltOutlinedIcon,
  administracion: ManageAccountsOutlinedIcon,
  shessai: BusinessIcon,
  sistema: SettingsOutlinedIcon,
};

export function categoriaDe(id) {
  return AYUDA_CATEGORIAS.find((c) => c.id === id);
}

export function renderNegritas(texto) {
  return texto.split(/(\*\*.+?\*\*)/g).map((parte, i) => {
    const m = parte.match(/^\*\*(.+)\*\*$/);
    return m ? <b key={i}>{m[1]}</b> : <React.Fragment key={i}>{parte}</React.Fragment>;
  });
}

export function fmtFecha(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
}

// `tipo` decide cómo se pintan los pasos: 'instructiva' es una secuencia de
// clics (círculo numerado + línea de conexión, "hacer esto y luego esto"),
// 'explicativa' es una lista de datos/afirmaciones sin orden de ejecución
// (punto sin numerar, sin conector).
export default function ArticuloContenido({ articulo, onAbrirArticulo, isLight }) {
  if (!articulo) return null;
  const cat = categoriaDe(articulo.categoria);
  const textoSecundario = isLight ? "#666" : "rgba(255,255,255,0.6)";
  const textoTerciario = isLight ? "#999" : "rgba(255,255,255,0.4)";
  const bordeSuave = isLight ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.08)";
  const fondoAcento = isLight ? "rgba(3,153,255,0.07)" : "rgba(3,153,255,0.12)";

  return (
    <Box>
      <Box sx={{ fontSize: 11, fontWeight: 700, color: "#0399FF", textTransform: "uppercase", letterSpacing: "0.6px", mb: 1 }}>
        {cat?.nombre}
      </Box>
      <Box sx={{ fontSize: 21, fontWeight: 700, letterSpacing: "-0.3px", lineHeight: 1.25, color: isLight ? "#111" : "rgba(255,255,255,0.92)", mb: 1 }}>
        {articulo.titulo}
      </Box>
      <Box sx={{ fontSize: 11.5, color: textoTerciario, mb: 2.5 }}>
        Actualizado el {fmtFecha(articulo.actualizado)}
      </Box>

      <Box sx={{ fontSize: 13.5, lineHeight: 1.65, color: textoSecundario, background: fondoAcento, borderRadius: "12px", p: "14px 16px", mb: 3.25 }}>
        {renderNegritas(articulo.contexto)}
      </Box>

      {articulo.tipo === "instructiva" ? (
        <Box component="ol" sx={{ listStyle: "none", m: 0, mb: 3.25, p: 0 }}>
          {articulo.pasos.map((p, i) => (
            <Box key={i} sx={{
              display: "grid", gridTemplateColumns: "26px 1fr", gap: "12px", pb: "18px", position: "relative",
              "&::before": i < articulo.pasos.length - 1 ? {
                content: '""', position: "absolute", left: "12px", top: "26px", bottom: "-2px", width: "1px", background: bordeSuave,
              } : undefined,
            }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: "50%", background: "#0399FF", color: "#fff",
                fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                {i + 1}
              </Box>
              <Box sx={{ fontSize: 13.5, lineHeight: 1.6, color: isLight ? "#111" : "rgba(255,255,255,0.88)", pt: "2px" }}>
                {renderNegritas(p)}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", mb: 3.25 }}>
          {articulo.pasos.map((p, i) => (
            <Box key={i} sx={{ display: "flex", gap: "10px" }}>
              <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: textoTerciario, flexShrink: 0, mt: "8px" }} />
              <Box sx={{ fontSize: 13.5, lineHeight: 1.6, color: isLight ? "#111" : "rgba(255,255,255,0.88)" }}>
                {renderNegritas(p)}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {articulo.rama && (
        <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start", background: fondoAcento, borderRadius: "12px", p: "14px 16px", mb: 3.75 }}>
          <AltRouteOutlinedIcon sx={{ fontSize: 16, color: "#0399FF", flexShrink: 0, mt: "2px" }} />
          <Box sx={{ fontSize: 13, lineHeight: 1.55, color: isLight ? "#111" : "rgba(255,255,255,0.88)" }}>
            {renderNegritas(articulo.rama)}
          </Box>
        </Box>
      )}

      {articulo.verTambien && articulo.verTambien.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: textoTerciario, textTransform: "uppercase", letterSpacing: "0.6px", mb: 1 }}>
            Ver también
          </Box>
          {articulo.verTambien.map((v) => (
            <Box
              key={v.slug} component="button" onClick={() => onAbrirArticulo(v.slug)}
              sx={{
                display: "block", width: "100%", textAlign: "left", border: `0.5px solid ${bordeSuave}`,
                background: isLight ? "#fff" : "rgba(255,255,255,0.03)", borderRadius: "8px", p: "10px 12px", mb: "6px",
                cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: "#0399FF",
                "&:hover": { borderColor: "#0399FF" },
              }}
            >
              {v.titulo}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
